import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The chat inside a meeting: the lines so far, polled every few seconds, and a
 * way to add one. Polling rather than a socket because guests have none, and one
 * way of delivering a message for everybody is one way to get wrong.
 *
 * `unread` counts lines from other people that arrived while the chat was not
 * open, for the badge on its button.
 *
 * `onNotice` is called once for each notice the meeting addresses to this
 * person - today, "somebody is waiting to share their screen". Notices that
 * were already old when the chat was first loaded are history, not news.
 */

const POLL_MS = 3000;
const FRESH_MS = 30000;

export const useMeetingChat = (client, { open, onNotice } = {}) => {
    const [messages, setMessages] = useState([]);
    const [unread, setUnread] = useState(0);
    const [error, setError] = useState('');
    const lastAt = useRef(null);
    const openRef = useRef(open);
    const noticeRef = useRef(onNotice);
    const seenNotices = useRef(new Set());

    noticeRef.current = onNotice;

    useEffect(() => {
        openRef.current = open;
        if (open) setUnread(0);
    }, [open]);

    const merge = useCallback((incoming) => {
        if (!incoming.length) return;
        lastAt.current = incoming[incoming.length - 1].createdAt;

        incoming.forEach((m) => {
            if (m.kind !== 'notice' || !m.toMe || seenNotices.current.has(m._id)) return;
            seenNotices.current.add(m._id);
            if (Date.now() - new Date(m.createdAt).getTime() < FRESH_MS) noticeRef.current?.(m);
        });

        setMessages((current) => {
            const known = new Set(current.map((m) => m._id));
            const fresh = incoming.filter((m) => !known.has(m._id));
            if (fresh.length && !openRef.current) {
                const others = fresh.filter((m) => !m.mine).length;
                if (others) setUnread((n) => n + others);
            }
            return fresh.length ? [...current, ...fresh] : current;
        });
    }, []);

    useEffect(() => {
        if (!client) return undefined;
        let alive = true;
        let timer = null;

        const tick = async () => {
            try {
                const rows = await client.listMessages(lastAt.current || undefined);
                if (alive) { merge(rows); setError(''); }
            } catch {
                if (alive) setError('Chat is not reachable right now.');
            }
            if (alive) timer = setTimeout(tick, POLL_MS);
        };
        tick();
        return () => { alive = false; clearTimeout(timer); };
    }, [client, merge]);

    const send = useCallback(async (text) => {
        const sent = await client.sendMessage(text);
        merge([sent]);
    }, [client, merge]);

    return { messages, unread, error, send };
};

export default useMeetingChat;
